import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import { getBranches, getExpenses, createExpense, updateExpense } from '../utilities/api';
import '../masters/CategoryMaster.css';

const ExpensesList = () => {
    const toast = useRef(null);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [branchFilterOptions, setBranchFilterOptions] = useState([]);
    const [branchFilter, setBranchFilter] = useState('All');

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });

    // ── Add dialog ───────────────────────────────────────────
    const [addOpen, setAddOpen] = useState(false);
    const [addSaving, setAddSaving] = useState(false);
    const [newExpenseName, setNewExpenseName] = useState('');
    const [newBranchId, setNewBranchId] = useState(null);

    const canAdd = useMemo(() => newExpenseName.trim() && newBranchId, [newExpenseName, newBranchId]);

    const resetAddForm = () => { setNewExpenseName(''); setNewBranchId(null); };

    // ── Edit dialog ──────────────────────────────────────────
    const [editOpen, setEditOpen] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [expenseName, setExpenseName] = useState('');
    const [branchId, setBranchId] = useState(null);

    const canSave = useMemo(() => expenseName.trim() && branchId, [expenseName, branchId]);

    // ── Filtered rows ────────────────────────────────────────
    const filteredRows = useMemo(() => {
        if (!Array.isArray(rows)) return [];
        if (!branchFilter || branchFilter === 'All') return rows;
        return rows.filter((r) => String(r.branch_name || '') === String(branchFilter));
    }, [rows, branchFilter]);

    const normalizeRows = (res) => {
        const data = res?.expnses ?? res?.expenses ?? res?.data ?? res;
        const list = Array.isArray(data) ? data : [];
        return list.map((r, i) => ({ ...r, row_id: r.expa_id ?? r.id ?? `exp-${i}` }));
    };

    // ── Data loading ─────────────────────────────────────────
    const loadBranches = useCallback(async () => {
        const clientId = localStorage.getItem('clientid');
        const branches = await getBranches(clientId);
        const arr = Array.isArray(branches) ? branches : [];
        setBranchOptions(arr.map((b) => ({
            label: b.branch_name || b.branchname || b.name || String(b.branch_id),
            value: String(b.branch_id)
        })));
    }, []);

    const loadExpenses = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getExpenses();
            const normalized = normalizeRows(res);
            setRows(normalized);

            const uniqueByName = new Map();
            const uniqueById = new Map();
            normalized.forEach((r) => {
                const bname = String(r?.branch_name || '').trim();
                const bid = r?.branch_id;
                if (bname && !uniqueByName.has(bname)) uniqueByName.set(bname, { label: bname, value: bname });
                if (bid != null) {
                    const idKey = String(bid);
                    if (!uniqueById.has(idKey)) uniqueById.set(idKey, { label: bname || `Branch ${idKey}`, value: idKey });
                }
            });
            setBranchFilterOptions(Array.from(uniqueByName.values()));
            const dynamicBranchOptions = Array.from(uniqueById.values());
            if (dynamicBranchOptions.length) setBranchOptions(dynamicBranchOptions);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Could not load expenses' });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await loadBranches();
                if (!cancelled) await loadExpenses();
            } catch {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [loadBranches, loadExpenses]);

    // ── Add expense ──────────────────────────────────────────
    const handleAddSubmit = async () => {
        if (!canAdd) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please enter expense name and select branch' });
            return;
        }
        setAddSaving(true);
        try {
            const res = await createExpense({ expanses_name: newExpenseName.trim(), branch_id: newBranchId });
            const ok = res?.status === true || res?.status === 'success' || res?.success === true;
            if (ok) {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: res?.message || 'Expense created successfully' });
                setAddOpen(false);
                resetAddForm();
                await loadExpenses();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || res?.error || 'Failed to create expense' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Request failed' });
        } finally {
            setAddSaving(false);
        }
    };

    // ── Edit expense ─────────────────────────────────────────
    const openEdit = (row) => {
        setEditRow(row);
        setExpenseName(row.expanses_name || row.expense_name || row.expa_name || '');
        setBranchId(row.branch_id != null ? String(row.branch_id) : branchOptions?.[0]?.value || null);
        setEditOpen(true);
    };

    const saveEdit = async () => {
        if (!canSave || !editRow) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fill all required fields' });
            return;
        }
        setEditSaving(true);
        try {
            const res = await updateExpense({ expa_id: editRow.expa_id ?? editRow.id, expanses_name: expenseName.trim(), branch_id: branchId });
            const ok = res?.status === true || res?.status === 'success' || res?.success === true;
            if (ok) {
                toast.current?.show({ severity: 'success', summary: 'Updated', detail: res?.message || 'Expense updated' });
                setEditOpen(false);
                await loadExpenses();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || res?.error || 'Update failed' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Request failed' });
        } finally {
            setEditSaving(false);
        }
    };

    const actionBody = (row) => (
        <Button icon="pi pi-pencil" rounded text className="p-button-warning" onClick={() => openEdit(row)} tooltip="Edit" tooltipOptions={{ position: 'top' }} />
    );

    // ── Render ───────────────────────────────────────────────
    return (
        <div className="masters-page-outer">
            <Toast ref={toast} />

            <div className="masters-page-header">
                <h2>Expenses List</h2>
                <p>View and manage expense types across branches</p>
            </div>

            {/* Filter card */}
            <Card className="mb-3 bill-reports-filter-card">
                <div className="flex flex-wrap gap-3 align-items-end bill-reports-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 220px', minWidth: '220px' }}>
                        <label className="mb-2 font-medium text-sm">Branch</label>
                        <Dropdown value={branchFilter} options={[{ label: 'All Branches', value: 'All' }, ...branchFilterOptions]} onChange={(e) => setBranchFilter(e.value)} placeholder="All Branches" className="w-full" showClear={false} />
                    </div>
                    <div style={{ flex: '0 0 auto' }}>
                        <Button label="Reset" icon="pi pi-refresh" className="p-button-secondary" onClick={() => setBranchFilter('All')} />
                    </div>
                </div>
            </Card>

            {/* Data card */}
            <Card className="bill-reports-container">
                <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                    <div className="flex align-items-center gap-2">
                        <h3 className="text-xl font-semibold m-0">Expenses</h3>
                        {!loading && (
                            <span style={{ fontSize: '0.82rem', color: '#6b7280', background: '#f3f4f6', borderRadius: '12px', padding: '0.2rem 0.7rem', fontWeight: 600 }}>
                                {filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left" style={{ minWidth: '200px' }}>
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={(e) => { setGlobalFilterValue(e.target.value); setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } }); }} placeholder="Search expenses..." className="w-full" />
                        </span>
                        <Button label="Add Expense" icon="pi pi-plus" className="p-button-primary" onClick={() => { resetAddForm(); setAddOpen(true); }} />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <div className="sub-masters-table-wrap">
                        <DataTable
                            value={filteredRows}
                            filters={filters}
                            globalFilterFields={['expanses_name', 'branch_name']}
                            dataKey="row_id" paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            showGridlines tableStyle={{ minWidth: '30rem' }}
                            className="p-datatable-sm sub-masters-table"
                            emptyMessage="No expenses found"
                        >
                            <Column header="Sl. No" body={(_, opt) => (opt?.rowIndex ?? 0) + 1} style={{ width: '70px' }} />
                            <Column field="expanses_name" header="Expense Name" body={(r) => r.expanses_name || r.expense_name || r.expa_name || '—'} sortable />
                            <Column field="branch_name" header="Branch" body={(r) => r.branch_name || r.branch_id || '—'} sortable />
                            <Column header="Actions" body={actionBody} style={{ width: '90px', textAlign: 'center' }} />
                        </DataTable>
                    </div>
                )}
            </Card>

            {/* ── Add Expense Dialog ─────────────────────────── */}
            <Dialog
                header={<div className="sub-dialog-header"><span className="sub-dialog-header-icon"><i className="pi pi-plus" /></span><span className="sub-dialog-header-text">Add Expense</span></div>}
                visible={addOpen}
                style={{ width: 'min(480px, 96vw)' }}
                onHide={() => { setAddOpen(false); resetAddForm(); }}
                className="sub-master-dialog"
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Clear" icon="pi pi-refresh" className="sub-dialog-cancel" onClick={resetAddForm} disabled={addSaving} />
                        <Button label="Submit" icon="pi pi-check" className="sub-dialog-save" onClick={handleAddSubmit} loading={addSaving} disabled={!canAdd} />
                    </div>
                }
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12">
                        <label className="font-medium">Branch</label>
                        <Dropdown value={newBranchId} options={branchOptions} onChange={(e) => setNewBranchId(e.value)} placeholder="Select branch" filter showClear />
                    </div>
                    <div className="field col-12">
                        <label className="font-medium">Expense Name</label>
                        <InputText value={newExpenseName} onChange={(e) => setNewExpenseName(e.target.value)} placeholder="Enter expense name" />
                    </div>
                </div>
            </Dialog>

            {/* ── Edit Expense Dialog ────────────────────────── */}
            <Dialog
                header={<div className="sub-dialog-header"><span className="sub-dialog-header-icon"><i className="pi pi-pencil" /></span><span className="sub-dialog-header-text">Edit Expense</span></div>}
                visible={editOpen}
                style={{ width: 'min(480px, 96vw)' }}
                onHide={() => setEditOpen(false)}
                className="sub-master-dialog"
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancel" className="sub-dialog-cancel" onClick={() => setEditOpen(false)} />
                        <Button label="Save" icon="pi pi-check" className="sub-dialog-save" onClick={saveEdit} loading={editSaving} disabled={!canSave} />
                    </div>
                }
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12">
                        <label className="font-medium">Branch</label>
                        <Dropdown value={branchId} options={branchOptions} onChange={(e) => setBranchId(e.value)} placeholder="Select branch" filter showClear />
                    </div>
                    <div className="field col-12">
                        <label className="font-medium">Expense Name</label>
                        <InputText value={expenseName} onChange={(e) => setExpenseName(e.target.value)} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ExpensesList;
